// Teams API fns — carved verbatim from `src/api/scribeAPIV2.ts` (which now
// re-exports from here) as OT2a, the first of the two ratified D1 route
// cohorts (`monorepo-workspace-data-core`; the 3 `organization/:id/teams/*`
// nested routes are cohort 2). See organization.ts for the transport-registry
// rationale (E5, ARCFE-805) — `http()` returns the app's real `scribeHttpV2`
// singleton, so request behaviour (including the frozen `/teams/` trailing
// slash on create/list) is byte-identical. `getSessionsByTeamMemberId` and
// `getTeamSessionsForAssistantView` deliberately stayed in the hub: their
// response types are Session-owned composites and `workspace -> session` is
// not an admitted edge (P0.4-class ruling pending).

import { getScribeHttpClients } from '@heidi/api/client';
import { MFAMethod, type components } from '@heidi/schema/ml-scribe';
import {
  type BatchTeamInvitationsResponse,
  type CreateInvitationResponse,
  type GetJoinableTeamsResponse,
  type JoinedTeamsResponse,
  type ListPendingTeamInvitationsResponse,
  type TeamBillingResponse,
  type TeamMemberRelationsResponse,
  type TeamResponse,
  type UpdateTeamRequest,
} from '../../contracts';
import { type WorkspaceHttpClient } from '../config';

const http = (): WorkspaceHttpClient => getScribeHttpClients().scribeHttpV2;

// The batch-invite endpoint is the module's ONE v2.1 caller (ARCFE-1041): it
// was carved out of `src/api/scribeAPIV2_1.ts`, a different hub from the
// v2 cohort above, and v2.1 is a distinct base URL — not a synonym. Routing it
// through `http()` would silently retarget the request, so the client is named
// separately here. `user.ts` is the precedent for two transports in one module.
const httpV2_1 = (): WorkspaceHttpClient => getScribeHttpClients().scribeHttpV2_1;

// Same pick the deprecated barrel makes — kept module-private so the type has
// exactly one public definition (the schema's).
type UserRole = components['schemas']['UserRole'];
type RequestEvidenceAccessResponse =
  components['schemas']['RequestEvidenceAccessResponse'];

/** @domain workspace */
export const acceptTeamInvitation = (teamId: string) => {
  return http().post(`/teams/${teamId}/invitations/accept`);
};

// PROVISIONAL: untyped until the decline route lands in generated `@heidi/schema` `paths`.
/** @domain workspace */
export const declineTeamInvitation = (teamId: string) => {
  return http().post(`/teams/${teamId}/invitations/decline`);
};

// Only invitations the accept guard would currently allow.
/** @domain workspace */
export const listPendingTeamInvitations = () => {
  return http().get<ListPendingTeamInvitationsResponse>('/teams/invitations/pending');
};

// teams API
/** @domain workspace */
export const createTeam = (name: string) => {
  return http().post<TeamResponse>(`/teams/`, { name });
};

/** @domain workspace */
export const createTeamInvitationLink = (teamId: string, email: string) => {
  return http().post<CreateInvitationResponse>(`/teams/${teamId}/invitations`, {
    email,
  });
};

/** @domain workspace */
export const createTeamInvitationLinkWithRoleIds = (
  teamId: string,
  email: string,
  roleIds: string[]
) => {
  return http().post<CreateInvitationResponse>(`/teams/${teamId}/invitations`, {
    email,
    role_ids: roleIds,
  });
};

/** @domain workspace */
export const deleteTeam = (teamId: string) => {
  return http().delete(`/teams/${teamId}`);
};

/** @domain workspace */
export const deleteTeamMember = (teamId: string, memberId: string) => {
  return http().delete(`/teams/${teamId}/member/${memberId}`);
};

/** @domain workspace */
/**
 * `GET /user/self-onboard/joinable-teams` — the teams a self-onboarding user is
 * eligible to join.
 *
 * IT LIVES HERE RATHER THAN IN `user.ts`, AND THE ROUTE'S TWO READINGS ARE WHY
 * THE FAMILY BLOCKER RECORDS IT AS A PLACEMENT AND NOT A DOMAIN QUESTION. The
 * first path segment is `/user` (which is how the ARCFE-1076 `/user/*` retag
 * reached it at all) while the PAYLOAD is a team list, which is this family's
 * noun and this module's cohort. Both readings put it inside workspace, so they
 * differ only on which file holds it — and the response type is the team-shaped
 * one, so it sits with the teams.
 *
 * @domain workspace
 */
export const getJoinableTeams = () => {
  return http().get<GetJoinableTeamsResponse>(`/user/self-onboard/joinable-teams`);
};

/** @domain workspace */
export const getJoinedTeams = () => {
  return http().get<JoinedTeamsResponse>(`/teams/`);
};

/** @domain workspace */
export const getTeamAnalyticsDashboard = (teamId: string) => {
  return http().get<{ url: string }>(`/teams/${teamId}/analytics-dashboard`);
};

/** @domain workspace */
export const getTeamBillingDetails = (teamId: string) => {
  return http().get<TeamBillingResponse>(`/teams/${teamId}/billing`);
};

/** @domain workspace */
export const getTeamDetails = (teamId: string) => {
  return http().get<TeamResponse>(`/teams/${teamId}`);
};

/** @domain workspace */
export const getTeamMembersWithRelationship = (teamId: string) => {
  return http().get<TeamMemberRelationsResponse>(`/teams/${teamId}/members`);
};

/** @domain workspace */
export const resendTeamInvitation = (teamId: string, invitationId: string) => {
  return http().post<CreateInvitationResponse>(
    `/teams/${teamId}/invitations/${invitationId}/resend`
  );
};

/**
 * Batch team invitations — the ONLY v2.1 fn in this module (see `httpV2_1`).
 * Carved verbatim from `src/api/scribeAPIV2_1.ts` (which now re-exports) at
 * ARCFE-1041. Frozen oddities: it shares the `/teams/{id}/invitations` path
 * with the three v2 invitation fns above but sends a DIFFERENT body
 * (`user_emails` plural, not `email`), and an omitted `roleIds` is sent as an
 * explicit empty array rather than dropped — the backend reads a missing key
 * differently from `[]`.
 *
 * @domain workspace
 */
export const sendBatchTeamInvitationsWithRoleIds = (
  teamId: string,
  emails: string[],
  roleIds?: string[]
) => {
  return httpV2_1().post<BatchTeamInvitationsResponse>(`/teams/${teamId}/invitations`, {
    user_emails: emails,
    role_ids: roleIds ?? [],
  });
};

/** @domain workspace */
export const toggleTeamConsent = (input: {
  consentEnabled: boolean;
  teamId: string;
  customConsentText?: string | null;
}) => {
  return http().patch(`/teams/${input.teamId}/consent`, {
    require_patient_consent: input.consentEnabled,
    custom_consent_text: input.customConsentText,
  });
};

/** @domain workspace */
export const toggleTeamMfa = (input: { mfaEnabled: boolean; teamId: string }) => {
  return http().patch(`/teams/${input.teamId}/mfa`, {
    mfa_method: MFAMethod.MFA_SMS_EMAIL,
    enable_mfa: input.mfaEnabled,
  });
};

/** @domain workspace */
export const toggleTeamReviewConfirmation = (input: {
  reviewConfirmationEnabled: boolean;
  teamId: string;
}) => {
  return http().patch(`/teams/${input.teamId}/review-confirmation`, {
    require_review_confirmation: input.reviewConfirmationEnabled,
  });
};

/** @domain workspace */
export const toggleTeamSessionRetention = (input: {
  sessionRetentionEnabled: boolean;
  retentionDays?: number;
  teamId: string;
}) => {
  return http().patch(`/teams/${input.teamId}/session-retention`, {
    session_retention_settings: {
      enabled: input.sessionRetentionEnabled,
      retention_days: input.retentionDays ? input.retentionDays : null,
    },
  });
};

/** @domain workspace */
export const toggleTeamSessionReviewWithRoleIds = (input: {
  sessionReviewEnabled: boolean;
  roles: UserRole[];
  roleIds: string[];
  teamId: string;
  name?: string;
}) => {
  return http().patch(`/teams/${input.teamId}`, {
    name: input.name,
    session_configuration: {
      allow_view_others_sessions: input.sessionReviewEnabled,
      roles_can_view_others_sessions: input.roles,
      role_ids_can_view_others_sessions: input.roleIds,
    },
  });
};

/** @domain workspace */
export const updateTeam = (teamId: string, name: string) => {
  return http().patch<TeamResponse>(`/teams/${teamId}`, { name });
};

/** @domain workspace */
export const updateTeamSettings = (input: {
  teamId: string;
  updateTeamRequest: UpdateTeamRequest;
}) => {
  return http().patch<TeamResponse>(`/teams/${input.teamId}`, {
    ...input.updateTeamRequest,
  });
};

// --- ARCFE-1105: the out-of-hub `/teams/` residue --------------------------
// Carved out of `src/features/evidence/accessRequests/hooks/
// useMutateRequestEvidenceAccess.ts`, which declared this route inline in a
// feature file — outside `API_HUB`, so invisible to the endpoint admission
// gate, to every family's `apiHubs` arm and to the endpoint baseline.
//
// FILED HERE BY THE RESOURCE-OWNER RULE (EN1), not by the directory it came
// from, which is evidence's — the same reading that put
// `src/features/sideBySideEvidence/api.ts` with chat, and the reading the
// ARCFE-1099 heal item on this family records. The route is `/teams/{id}/…`,
// `/teams/` is workspace's by measurement (workspace:31 · session:4 ·
// commerce:1), and a team-scoped write is a team write whatever it grants
// access to. ONLY THE TRANSPORT MOVED: the hook stays app-side, because its
// `onSuccess` hand-patches the `GET_USER` cache and calls
// `invalidateCurrentUser()` — a foreign cache reaction the package may not own.
// If the pods rule the RESOURCE evidence's, this fn moves rather than the row.

/** @domain workspace */
export const requestTeamEvidenceAccess = (input: {
  teamId: string;
  message?: string;
}) => {
  // The `message ? { message } : {}` body is wire behaviour, not style: the
  // backend distinguishes an absent message from an explicit `undefined`.
  return http().post<RequestEvidenceAccessResponse>(
    `/teams/${input.teamId}/request-evidence-access`,
    input.message ? { message: input.message } : {}
  );
};
